var __classPrivateFieldSet = (this && this.__classPrivateFieldSet) || function (receiver, state, value, kind, f) {
    if (kind === "m") throw new TypeError("Private method is not writable");
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a setter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot write private member to an object whose class did not declare it");
    return (kind === "a" ? f.call(receiver, value) : f ? f.value = value : state.set(receiver, value)), value;
};
var __classPrivateFieldGet = (this && this.__classPrivateFieldGet) || function (receiver, state, kind, f) {
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a getter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot read private member from an object whose class did not declare it");
    return kind === "m" ? f : kind === "a" ? f.call(receiver) : f ? f.value : state.get(receiver);
};
var _JoltConnection_wssEndpoint, _JoltConnection_fetchAuthToken, _JoltConnection_logger, _JoltConnection_retryTimeout, _JoltConnection_webSocket, _JoltConnection_pingIntervalDuration, _JoltConnection_pingInterval, _JoltConnection_pingTimeoutDuration, _JoltConnection_pingTimeout, _JoltConnection_connectPromise, _JoltConnection_handlePong, _JoltConnection_handleOnClose;
import { NullLogger } from "./Logger";
import { retryable } from "./retryable";
const DEFAULT_RETRY_TIMEOUT = 3000;
export const CLIENT_CLOSED = "Client triggered connection to close";
const CLOSING_DEAD_CONNECTION = "Closing connection for a websocket that we are no longer using";
export const TypedEventTarget = EventTarget;
export class JoltConnection extends TypedEventTarget {
    constructor(wssEndpoint, fetchAuthToken, { logger = new NullLogger(), pingInterval = 240000, retryTimeout = () => DEFAULT_RETRY_TIMEOUT, pingTimeout = 2000, } = {}) {
        super();
        _JoltConnection_wssEndpoint.set(this, void 0);
        _JoltConnection_fetchAuthToken.set(this, void 0);
        _JoltConnection_logger.set(this, void 0);
        _JoltConnection_retryTimeout.set(this, void 0);
        _JoltConnection_webSocket.set(this, void 0);
        _JoltConnection_pingIntervalDuration.set(this, void 0);
        _JoltConnection_pingInterval.set(this, void 0);
        _JoltConnection_pingTimeoutDuration.set(this, void 0);
        _JoltConnection_pingTimeout.set(this, void 0);
        _JoltConnection_connectPromise.set(this, void 0);
        this._setupPing = () => {
            __classPrivateFieldSet(this, _JoltConnection_pingInterval, setInterval(() => {
                try {
                    this.ping();
                }
                catch (error) {
                    // do nothing
                }
            }, __classPrivateFieldGet(this, _JoltConnection_pingIntervalDuration, "f")), "f");
        };
        _JoltConnection_handlePong.set(this, () => {
            if (__classPrivateFieldGet(this, _JoltConnection_pingTimeout, "f"))
                clearTimeout(__classPrivateFieldGet(this, _JoltConnection_pingTimeout, "f"));
        });
        _JoltConnection_handleOnClose.set(this, (reason) => {
            this.log("disconnected", reason);
            this._clearPingInterval();
            __classPrivateFieldSet(this, _JoltConnection_webSocket, undefined, "f");
            __classPrivateFieldSet(this, _JoltConnection_connectPromise, undefined, "f");
            __classPrivateFieldSet(this, _JoltConnection_pingInterval, undefined, "f");
            if (reason === CLOSING_DEAD_CONNECTION) {
                // do nothing
            }
            else {
                this._emitDisconnected({ intentional: reason === CLIENT_CLOSED });
            }
        });
        this._clearPingInterval = () => {
            if (__classPrivateFieldGet(this, _JoltConnection_pingInterval, "f")) {
                clearInterval(__classPrivateFieldGet(this, _JoltConnection_pingInterval, "f"));
            }
        };
        this._emitDisconnected = (detail) => {
            const event = new CustomEvent("jolt-client.disconnected", { detail });
            this.dispatchEvent(event);
        };
        this._handleConnect = (message, resolve) => {
            const connectionId = message.data.connectionId;
            resolve(connectionId);
            this.log("connected with ID:", connectionId);
        };
        this._handleSubscribed = (message) => {
            const event = new CustomEvent("jolt.subscribed", { detail: message });
            this.dispatchEvent(event);
        };
        this._handleUnsubscribed = (message) => {
            const event = new CustomEvent("jolt.unsubscribed", { detail: message });
            this.dispatchEvent(event);
        };
        this._handleMessage = (message) => {
            const event = new CustomEvent("jolt.message", { detail: message });
            this.dispatchEvent(event);
        };
        this._handleError = (message) => {
            this.log("Error:", message);
            const event = new CustomEvent("jolt.error", { detail: message });
            this.dispatchEvent(event);
        };
        this._reviveConnection = () => {
            if (__classPrivateFieldGet(this, _JoltConnection_webSocket, "f")) {
                __classPrivateFieldGet(this, _JoltConnection_webSocket, "f").onerror = null;
                __classPrivateFieldGet(this, _JoltConnection_webSocket, "f").onmessage = null;
                __classPrivateFieldGet(this, _JoltConnection_webSocket, "f").onopen = null;
                __classPrivateFieldGet(this, _JoltConnection_webSocket, "f").onclose = null;
                __classPrivateFieldGet(this, _JoltConnection_webSocket, "f").close();
            }
            __classPrivateFieldGet(this, _JoltConnection_handleOnClose, "f").call(this, "Server timed out");
        };
        __classPrivateFieldSet(this, _JoltConnection_wssEndpoint, wssEndpoint, "f");
        __classPrivateFieldSet(this, _JoltConnection_fetchAuthToken, fetchAuthToken, "f");
        __classPrivateFieldSet(this, _JoltConnection_logger, logger, "f");
        __classPrivateFieldSet(this, _JoltConnection_pingIntervalDuration, pingInterval, "f");
        __classPrivateFieldSet(this, _JoltConnection_pingTimeoutDuration, pingTimeout, "f");
        __classPrivateFieldSet(this, _JoltConnection_retryTimeout, retryTimeout, "f");
    }
    connect() {
        if (__classPrivateFieldGet(this, _JoltConnection_connectPromise, "f"))
            return __classPrivateFieldGet(this, _JoltConnection_connectPromise, "f");
        return (__classPrivateFieldSet(this, _JoltConnection_connectPromise, new Promise((resolve) => {
            this.log("Connecting...");
            retryable(async () => {
                const authToken = await __classPrivateFieldGet(this, _JoltConnection_fetchAuthToken, "f").call(this);
                return this._initiateWebSocket(authToken);
            }, {
                generateTimeout: __classPrivateFieldGet(this, _JoltConnection_retryTimeout, "f"),
                onError: (error, timeout) => {
                    this.log(`Failed to connect to the WebSocket. Retrying in ${timeout}ms`, error);
                },
            }).then(() => this._setupWebSocketListeners(resolve));
        }), "f"));
    }
    disconnect() {
        if (!__classPrivateFieldGet(this, _JoltConnection_webSocket, "f"))
            return;
        __classPrivateFieldGet(this, _JoltConnection_webSocket, "f").close(1000, CLIENT_CLOSED);
    }
    get connectionId() {
        return this.connect();
    }
    send(message) {
        if (!__classPrivateFieldGet(this, _JoltConnection_webSocket, "f"))
            throw new NoWebSocketError();
        const encodedMessage = JSON.stringify({
            ...message,
            data: JSON.stringify(message.data),
        });
        __classPrivateFieldGet(this, _JoltConnection_webSocket, "f").send(encodedMessage);
    }
    ping() {
        if (!__classPrivateFieldGet(this, _JoltConnection_webSocket, "f") || __classPrivateFieldGet(this, _JoltConnection_webSocket, "f").readyState !== __classPrivateFieldGet(this, _JoltConnection_webSocket, "f").OPEN)
            throw new NoWebSocketError();
        __classPrivateFieldGet(this, _JoltConnection_webSocket, "f").send(JSON.stringify({ event: "jolt.ping" }));
        __classPrivateFieldSet(this, _JoltConnection_pingTimeout, setTimeout(() => this._reviveConnection(), __classPrivateFieldGet(this, _JoltConnection_pingTimeoutDuration, "f")), "f");
    }
    // private
    _initiateWebSocket(authToken) {
        if (__classPrivateFieldGet(this, _JoltConnection_webSocket, "f")) {
            // Chrome is known to resurrect closed websockets, and we don't want that. So, um, *extra* close it!
            this.log("Closing existing WebSocket connection");
            __classPrivateFieldGet(this, _JoltConnection_webSocket, "f").close(1000, CLOSING_DEAD_CONNECTION);
        }
        this.log("Initiating WebSocket connection");
        __classPrivateFieldSet(this, _JoltConnection_webSocket, new WebSocket(`${__classPrivateFieldGet(this, _JoltConnection_wssEndpoint, "f")}?token=${authToken}`), "f");
        if (__classPrivateFieldGet(this, _JoltConnection_webSocket, "f").readyState === __classPrivateFieldGet(this, _JoltConnection_webSocket, "f").CLOSED) {
            throw new Error("Connection to WebSocket was unable to be established");
        }
    }
    _setupWebSocketListeners(resolve) {
        if (!__classPrivateFieldGet(this, _JoltConnection_webSocket, "f")) {
            throw new Error("[Jolt] attempted to setup websocket before initializing");
        }
        __classPrivateFieldGet(this, _JoltConnection_webSocket, "f").onerror = (event) => {
            this.log("WebSocket error:", event);
        };
        __classPrivateFieldGet(this, _JoltConnection_webSocket, "f").onopen = () => {
            this._setupPing();
        };
        __classPrivateFieldGet(this, _JoltConnection_webSocket, "f").onclose = ({ reason }) => {
            __classPrivateFieldGet(this, _JoltConnection_handleOnClose, "f").call(this, reason);
        };
        __classPrivateFieldGet(this, _JoltConnection_webSocket, "f").onmessage = (messageEvent) => {
            const message = this._normalizeMessage(messageEvent);
            if (!message)
                return;
            switch (message.event) {
                case "jolt.connected":
                    return this._handleConnect(message, resolve);
                case "jolt.error":
                    return this._handleError(message);
                case "jolt.subscribed":
                    return this._handleSubscribed(message);
                case "jolt.unsubscribed":
                    return this._handleUnsubscribed(message);
                case "jolt.pong":
                    return __classPrivateFieldGet(this, _JoltConnection_handlePong, "f").call(this);
                default:
                    return this._handleMessage(message);
            }
        };
    }
    _normalizeMessage(messageEvent) {
        try {
            const message = JSON.parse(messageEvent.data);
            try {
                return { ...message, data: JSON.parse(message.data) };
            }
            catch (e) {
                return { ...message, data: {} };
            }
        }
        catch (e) {
            this.log("Unparseable Event:", messageEvent);
            return null;
        }
    }
    log(...args) {
        __classPrivateFieldGet(this, _JoltConnection_logger, "f").log(...args);
    }
}
_JoltConnection_wssEndpoint = new WeakMap(), _JoltConnection_fetchAuthToken = new WeakMap(), _JoltConnection_logger = new WeakMap(), _JoltConnection_retryTimeout = new WeakMap(), _JoltConnection_webSocket = new WeakMap(), _JoltConnection_pingIntervalDuration = new WeakMap(), _JoltConnection_pingInterval = new WeakMap(), _JoltConnection_pingTimeoutDuration = new WeakMap(), _JoltConnection_pingTimeout = new WeakMap(), _JoltConnection_connectPromise = new WeakMap(), _JoltConnection_handlePong = new WeakMap(), _JoltConnection_handleOnClose = new WeakMap();
export class NoWebSocketError extends Error {
    constructor() {
        super("No websocket connected");
    }
}
//# sourceMappingURL=JoltConnection.js.map